import React, { useEffect, useRef, useState } from 'react';
import { AnimatedShinyText } from './ui/animated-shiny-text';

const PartnersSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const partnerLogos = [
    {
      name: "Ticketmaster",
      alt: "Ticketmaster",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/q32v96e5_ticketmaster.png",
      width: 153
    },
    {
      name: "DICE",
      alt: "DICE",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/0uh5huvu_Dice.png",
      width: 103
    },
    {
      name: "Resident Advisor",
      alt: "Resident Advisor / RA",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/8ow3u6ho_ResidentAdvisorRA.png",
      width: 103
    },
    {
      name: "Shotgun",
      alt: "Shotgun",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/xg6s8jgq_Shotgun.svg",
      width: 173
    },
    {
      name: "Fever",
      alt: "Fever",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/6sk8phka_Fever.png",
      width: 133
    },
    {
      name: "Kaboodle",
      alt: "Kaboodle",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/y24yu249_Kaboodle.png",
      width: 173
    },
    {
      name: "AXS",
      alt: "AXS",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/0jo0zbt6_AXS.png",
      width: 103
    },
    {
      name: "TicketSwap",
      alt: "TicketSwap",
      src: "https://customer-assets.emergentagent.com/job_festival-app-refresh/artifacts/9s2n9t66_ticketswap%20logo.png",
      width: 183
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2, // Trigger when 20% of the component is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before fully in view
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-black py-16 md:py-24">
      <div className="container mx-auto px-6 md:px-6">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-[36px] md:text-[48px] font-medium text-white mb-2 font-['Space_Grotesk']">
            Works Seamlessly
          </h2>
          <p className="text-[20px] md:text-[24px] text-white/60">
            Antix identifies events from major international and national ticketing platforms and promoters. And will intelligently learn to identify smaller platforms, local venues and promoters too.
          </p>
        </div>

        {/* Logo Grid */}
        <div className="max-w-6xl mx-auto">
          {/* Desktop: 4 across × 2 rows, Mobile: 2 across × 4 rows */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {partnerLogos.map((logo, index) => (
              <div
                key={logo.name}
                className={`
                  flex items-center justify-center 
                  transition-all duration-700 ease-out
                  ${isVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8'
                  }
                `}
                style={{
                  transitionDelay: `${index * 100}ms` // Stagger animation: 0ms, 100ms, 200ms, etc.
                }}
              >
                <div className="
                  w-full h-16 md:h-20 
                  flex items-center justify-center
                  opacity-70 hover:opacity-100 
                  transition-opacity duration-300 ease-in-out
                  group
                ">
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    style={{
                      width: `${logo.width}px`,
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                    className="
                      filter brightness-0 invert
                      group-hover:scale-105 
                      transition-transform duration-300 ease-in-out
                    "
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Platforms Count Lozenge */}
        <div className="mt-12 flex items-center justify-center">
          <div className="relative inline-flex items-center justify-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/30 transition-all duration-300 hover:bg-white/15">
            {/* Content */}
            <div className="relative z-10">
              <AnimatedShinyText
                className="inline-flex items-center justify-center transition ease-out hover:text-neutral-600 hover:duration-300 hover:dark:text-neutral-300"
                shimmerWidth={150}
              >
                <span className="mr-2 text-lg">🔎</span>
                <span className="text-sm font-medium text-white/90">Support for 40+ ticket platforms and counting</span>
              </AnimatedShinyText>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
